import React from "react";
import { MdEdit, MdDelete, MdRemoveRedEye, MdLock, MdLockOpen } from "react-icons/md";
import { usePhotoUrl } from "utils/usePhotoUrl";

const UserTable = ({ users, loading, onEdit, onDelete, onView, onActivate, onDeactivate }) => {

  const {getPhotoUrl} = usePhotoUrl();
  return (
    <div className="overflow-hidden rounded-lg shadow-sm border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-700"
            >
              Utilisateur
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 hidden md:table-cell"
            >
              Email
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 hidden lg:table-cell"
            >
              Rôle
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-center text-sm font-semibold text-gray-700"
            >
              Statut
            </th>
            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {loading && users.length === 0 ? (
            <tr>
              <td colSpan="5" className="px-3 py-6 text-center text-sm text-gray-500">
                <div className="flex justify-center">
                  <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </td>
            </tr>
          ) : users.length === 0 ? (
            <tr>
              <td colSpan="5" className="px-3 py-6 text-center text-sm text-gray-500">
                <p>Aucun utilisateur trouvé</p>
                <p className="text-xs text-gray-400 mt-1">
                  Créez un nouvel utilisateur en cliquant sur le bouton "Nouvel Utilisateur"
                </p>
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                <td className="py-4 pl-4 pr-3 text-sm">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      {user.photo ? (
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={getPhotoUrl(user.photo)}
                          alt={`Avatar de ${user.username}`}
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/40?text=?";
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium">
                          {user.username ? user.username.charAt(0).toUpperCase() : '?'}
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="font-medium text-gray-900">
                        {user.membre ? user.membre.nom_prenom_membre : user.username}
                      </div>
                      <div className="text-gray-500 text-xs">@{user.username}</div>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600 hidden md:table-cell">
                  {user.email}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600 hidden lg:table-cell">
                  {user.role ? (
                    <span 
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                        ${user.role.privilege === 1 ? 'bg-red-100 text-red-800' : 
                          user.role.privilege === 2 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-blue-100 text-blue-800'}`}
                    >
                      {user.role.role_name}
                    </span>
                  ) : "-"}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600 text-center">
                  {user.isActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                      Inactif
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onView(user)}
                      className="p-1.5 text-indigo-600 hover:text-indigo-900 rounded-md hover:bg-gray-100 transition-colors"
                      title="Voir les détails"
                    >
                      <MdRemoveRedEye className="h-5 w-5" />
                    </button>
                    {user.isActive ? (
                      <button
                        onClick={() => onDeactivate(user.user_id)}
                        className="p-1.5 text-yellow-600 hover:text-yellow-800 rounded-md hover:bg-gray-100 transition-colors"
                        title="Désactiver"
                      >
                        <MdLock className="h-5 w-5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => onActivate(user.user_id)}
                        className="p-1.5 text-green-600 hover:text-green-800 rounded-md hover:bg-gray-100 transition-colors"
                        title="Activer"
                      >
                        <MdLockOpen className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(user)}
                      className="p-1.5 text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-100 transition-colors"
                      title="Modifier"
                    >
                      <MdEdit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onDelete(user)}
                      className="p-1.5 text-red-500 hover:text-red-700 rounded-md hover:bg-gray-100 transition-colors"
                      title="Supprimer"
                    >
                      <MdDelete className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;